import * as THREE from "three";
import { VrGameUi, type VrGameBridge } from "./vrGameUi";

type TextureCacheEntry = {
  promise: Promise<THREE.Texture | null>;
  texture: THREE.Texture | null;
  stale: boolean;
};

export class PanoramaViewer {
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  private readonly material = new THREE.MeshBasicMaterial({ color: 0xffffff });
  private readonly loader = new THREE.TextureLoader();
  private readonly fallbackTexture = this.createFallbackTexture();
  private readonly geometry = new THREE.SphereGeometry(500, 96, 48);
  private textureCache = new Map<string, TextureCacheEntry>();
  private loadRequestId = 0;
  private activeTextureUrl: string | null = null;
  private disposed = false;
  private lon = 180;
  private lat = 0;
  private dragStart = { x: 0, y: 0, lon: 180, lat: 0 };
  private activePointerId: number | null = null;
  private dragging = false;
  private velocityLon = 0;
  private velocityLat = 0;
  private lastMoveTime = performance.now();
  private lastFrameTime = performance.now();
  private resizeObserver: ResizeObserver;
  private afterRenderListeners = new Set<() => void>();
  private introFrameId: number | null = null;
  private introResolve: (() => void) | null = null;
  private compassEl: HTMLDivElement;
  private compassFaceEl: HTMLDivElement;
  private compassFadeTimeout: number | null = null;
  private kenBurnsFrameId: number | null = null;
  private vrButton: HTMLButtonElement | null = null;
  private vrGame: VrGameUi | null = null;
  private sphereMesh!: THREE.Mesh;
  private cubeRT: THREE.WebGLCubeRenderTarget | null = null;

  constructor(private readonly container: HTMLElement) {
    // near = 0.1 (not 1.0) so the in-VR HUD panels at ~1.2 m are never clipped by
    // the near plane; the lone background sphere has no depth-precision concerns.
    this.camera = new THREE.PerspectiveCamera(86, this.getAspectRatio(), 0.1, 1200);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.domElement.className = "panorama-canvas";
    this.renderer.domElement.style.touchAction = "none";
    this.container.append(this.renderer.domElement);

    // WebXR: the inverted-sphere panorama doubles as an immersive 360° scene on
    // headsets (e.g. the Meta Quest Browser). Enabling xr lets the same render
    // loop drive the headset; setupVR() adds an "Enter VR" button only when an
    // immersive-vr runtime is actually present (so desktops are unaffected).
    this.renderer.xr.enabled = true;

    this.geometry.scale(-1, 1, 1);
    this.sphereMesh = new THREE.Mesh(this.geometry, this.material);
    this.scene.add(this.sphereMesh);

    // In VR, swap the inverted-sphere panorama for a cubemap skybox: same image, no
    // equirectangular pole pinch / seam (the "overlapping" artifact). Desktop keeps the
    // sphere; conversion failures fall back to it automatically.
    this.renderer.xr.addEventListener("sessionstart", () => this.refreshVrBackground());
    this.renderer.xr.addEventListener("sessionend", () => this.disableVrBackground());

    this.applyTexture(this.fallbackTexture, null);

    // Compass overlay — orients the user inside the 360° scene. Fades after a
    // short visible window; re-appears on drag.
    this.compassEl = document.createElement("div");
    this.compassEl.className = "panorama-compass";
    this.compassFaceEl = document.createElement("div");
    this.compassFaceEl.className = "panorama-compass-face";
    this.compassFaceEl.innerHTML =
      '<span class="compass-mark compass-n">N</span>' +
      '<span class="compass-mark compass-e">E</span>' +
      '<span class="compass-mark compass-s">S</span>' +
      '<span class="compass-mark compass-w">W</span>';
    this.compassEl.appendChild(this.compassFaceEl);
    this.container.appendChild(this.compassEl);
    this.scheduleCompassFade();

    this.bindEvents();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.container);
    this.renderer.setAnimationLoop(() => this.render());
    // VR mode is hidden for now on both desktop and mobile: setupVR() is what adds
    // the "Enter VR" button in the lower-right corner, so leaving it uncalled keeps
    // the whole VR path dormant. Re-enable by uncommenting this line.
    // void this.setupVR();
  }

  // --- VR mode (disabled) ------------------------------------------------------
  // The "Enter VR" button and the immersive-vr session handshake are commented out
  // so no VR entry point shows up on desktop or mobile. Restore by uncommenting
  // both methods below plus the `void this.setupVR()` call in the constructor.
  //
  // private async setupVR(): Promise<void> {
  //   const xr = (navigator as unknown as { xr?: { isSessionSupported?: (m: string) => Promise<boolean>; requestSession: (m: string, o?: unknown) => Promise<unknown> } }).xr;
  //   if (!xr || typeof xr.isSessionSupported !== "function") {
  //     return;
  //   }
  //   let supported = false;
  //   try {
  //     supported = await xr.isSessionSupported("immersive-vr");
  //   } catch {
  //     supported = false;
  //   }
  //   if (!supported || this.disposed) {
  //     return;
  //   }
  //
  //   const btn = document.createElement("button");
  //   btn.type = "button";
  //   btn.className = "panorama-vr-btn";
  //   btn.textContent = "Enter VR";
  //   Object.assign(btn.style, {
  //     position: "absolute",
  //     right: "12px",
  //     bottom: "12px",
  //     zIndex: "6",
  //     padding: "10px 16px",
  //     borderRadius: "999px",
  //     border: "0",
  //     cursor: "pointer",
  //     font: "600 14px system-ui, sans-serif",
  //     color: "#04121f",
  //     background: "rgba(120, 230, 255, 0.92)",
  //     boxShadow: "0 2px 10px rgba(0, 0, 0, 0.4)"
  //   } as Partial<CSSStyleDeclaration>);
  //   btn.addEventListener("click", () => {
  //     const session = this.renderer.xr.getSession();
  //     if (session) {
  //       void session.end();
  //     } else {
  //       void this.enterVR();
  //     }
  //   });
  //   this.container.appendChild(btn);
  //   this.vrButton = btn;
  // }
  //
  // private async enterVR(): Promise<void> {
  //   const xr = (navigator as unknown as { xr?: { requestSession: (m: string, o?: unknown) => Promise<unknown> } }).xr;
  //   if (!xr) {
  //     return;
  //   }
  //   try {
  //     const session = await xr.requestSession("immersive-vr", {
  //       optionalFeatures: ["local-floor", "bounded-floor"]
  //     });
  //     await this.renderer.xr.setSession(session as Parameters<typeof this.renderer.xr.setSession>[0]);
  //     if (this.vrButton) {
  //       this.vrButton.textContent = "Exit VR";
  //     }
  //     (session as { addEventListener: (t: string, cb: () => void) => void }).addEventListener("end", () => {
  //       if (this.vrButton) {
  //         this.vrButton.textContent = "Enter VR";
  //       }
  //     });
  //   } catch (err) {
  //     console.error("Could not start VR session", err);
  //   }
  // }

  /** Wire the in-headset game UI (timeline → map → reveal) to the 2D game logic. */
  attachVrGame(bridge: VrGameBridge): void {
    if (this.vrGame) {
      return;
    }
    this.vrGame = new VrGameUi(this.renderer, this.scene, bridge);
  }

  private scheduleCompassFade(): void {
    this.compassEl.classList.remove("fading");
    if (this.compassFadeTimeout !== null) {
      window.clearTimeout(this.compassFadeTimeout);
    }
    this.compassFadeTimeout = window.setTimeout(() => {
      this.compassEl.classList.add("fading");
      this.compassFadeTimeout = null;
    }, 4000);
  }

  private updateCompass(): void {
    // Camera lon=180 is treated as "north" (the panorama's reference forward).
    // As the user pans right, the compass face rotates left so N stays in the
    // panorama's north — giving the player a sense of which way they're facing.
    const offset = ((180 - this.lon) % 360 + 360) % 360;
    this.compassFaceEl.style.transform = `rotate(${offset}deg)`;
  }

  async loadTexture(imageUrl: string): Promise<void> {
    this.cancelIntro();
    const requestId = ++this.loadRequestId;
    // Preload first — the current scene stays on screen during the fetch
    // instead of flashing through the LatLongAgo fallback texture. The
    // camera reset and texture swap happen together once the bytes are in.
    const texture = await this.preloadTexture(imageUrl);
    if (requestId !== this.loadRequestId) {
      return;
    }
    this.resetView();
    this.applyTexture(texture ?? this.fallbackTexture, texture ? imageUrl : null);
  }

  preloadTexture(imageUrl: string): Promise<THREE.Texture | null> {
    if (!imageUrl) {
      return Promise.resolve(this.fallbackTexture);
    }

    const cachedTexture = this.textureCache.get(imageUrl);
    if (cachedTexture) {
      return cachedTexture.promise;
    }

    const entry: TextureCacheEntry = {
      promise: Promise.resolve(null),
      texture: null,
      stale: false
    };

    entry.promise = new Promise<THREE.Texture | null>((resolve) => {
      this.loader.load(
        imageUrl,
        (texture) => {
          this.configureTexture(texture);
          if (this.disposed || entry.stale || this.textureCache.get(imageUrl) !== entry) {
            texture.dispose();
            resolve(null);
            return;
          }

          entry.texture = texture;
          resolve(texture);
        },
        undefined,
        () => {
          if (this.textureCache.get(imageUrl) === entry) {
            this.textureCache.delete(imageUrl);
          }
          resolve(null);
        }
      );
    });

    this.textureCache.set(imageUrl, entry);
    return entry.promise;
  }

  showFallback(): void {
    this.cancelIntro();
    this.cancelKenBurns();
    this.loadRequestId++;
    this.resetView();
    this.applyTexture(this.fallbackTexture, null);
  }

  retainTextures(imageUrls: Iterable<string | null | undefined>): void {
    const keep = new Set(Array.from(imageUrls).filter((url): url is string => Boolean(url)));
    if (this.activeTextureUrl) {
      keep.add(this.activeTextureUrl);
    }

    for (const [url, entry] of this.textureCache) {
      if (keep.has(url)) {
        continue;
      }

      this.textureCache.delete(url);
      entry.stale = true;
      if (entry.texture && this.material.map !== entry.texture) {
        entry.texture.dispose();
        entry.texture = null;
      }
    }
  }

  // Cinematic intro: slow camera pan over the loaded panorama. Promise
  // resolves when the pan completes naturally OR the user interrupts by
  // dragging. Calling again cancels the previous animation.
  playIntro(durationMs: number = 2000, panDegrees: number = 30): Promise<void> {
    this.cancelIntro();
    const startLon = this.lon - panDegrees / 2;
    const endLon = this.lon + panDegrees / 2;
    this.lon = startLon;
    this.velocityLon = 0;
    this.velocityLat = 0;
    const startedAt = performance.now();

    return new Promise<void>((resolve) => {
      this.introResolve = resolve;
      const tick = () => {
        const t = Math.min(1, (performance.now() - startedAt) / durationMs);
        // ease-out cubic — slow glide that decelerates into rest
        const eased = 1 - Math.pow(1 - t, 3);
        this.lon = startLon + (endLon - startLon) * eased;
        this.velocityLon = 0;
        if (t < 1) {
          this.introFrameId = window.requestAnimationFrame(tick);
        } else {
          this.introFrameId = null;
          const r = this.introResolve;
          this.introResolve = null;
          r?.();
        }
      };
      this.introFrameId = window.requestAnimationFrame(tick);
    });
  }

  private cancelIntro(): void {
    if (this.introFrameId !== null) {
      window.cancelAnimationFrame(this.introFrameId);
      this.introFrameId = null;
    }
    if (this.introResolve) {
      const r = this.introResolve;
      this.introResolve = null;
      r();
    }
  }

  /**
   * Theatre Mode: drive a slow Ken-Burns-style pan + zoom over the segment's
   * duration. Honours prefers-reduced-motion by holding the start position.
   */
  setKenBurns(opts: {
    startYaw: number;
    endYaw: number;
    startZoom: number;
    endZoom: number;
    durationSec: number;
  }): { cancel: () => void } {
    this.cancelKenBurns();
    this.cancelIntro();

    const baseFov = 86;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const startedAt = performance.now();
    const durationMs = Math.max(1, opts.durationSec * 1000);

    if (reduceMotion) {
      this.lon = opts.startYaw;
      this.velocityLon = 0;
      this.velocityLat = 0;
      this.camera.fov = baseFov / opts.startZoom;
      this.camera.updateProjectionMatrix();
      return { cancel: () => {} };
    }

    this.lon = opts.startYaw;
    this.velocityLon = 0;
    this.velocityLat = 0;
    this.camera.fov = baseFov / opts.startZoom;
    this.camera.updateProjectionMatrix();

    const tick = () => {
      const t = Math.min(1, (performance.now() - startedAt) / durationMs);
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      this.lon = opts.startYaw + (opts.endYaw - opts.startYaw) * eased;
      const zoom = opts.startZoom + (opts.endZoom - opts.startZoom) * eased;
      this.camera.fov = baseFov / zoom;
      this.camera.updateProjectionMatrix();
      this.velocityLon = 0;
      this.velocityLat = 0;
      if (t < 1) {
        this.kenBurnsFrameId = window.requestAnimationFrame(tick);
      } else {
        this.kenBurnsFrameId = null;
      }
    };
    this.kenBurnsFrameId = window.requestAnimationFrame(tick);
    return { cancel: () => this.cancelKenBurns() };
  }

  cancelKenBurns(): void {
    if (this.kenBurnsFrameId !== null) {
      window.cancelAnimationFrame(this.kenBurnsFrameId);
      this.kenBurnsFrameId = null;
    }
  }

  resize(): void {
    if (this.renderer.xr.isPresenting) {
      return; // the XR runtime manages the drawing-buffer size while presenting
    }
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setSize(width, height);
  }

  getCameraState(): { lon: number; lat: number; fov: number; aspect: number } {
    return {
      lon: this.lon,
      lat: this.lat,
      fov: this.camera.fov,
      aspect: this.camera.aspect
    };
  }

  onAfterRender(callback: () => void): () => void {
    this.afterRenderListeners.add(callback);
    return () => this.afterRenderListeners.delete(callback);
  }

  dispose(): void {
    this.disposed = true;
    this.vrButton?.remove();
    this.resizeObserver.disconnect();
    this.renderer.setAnimationLoop(null);
    this.retainTextures([]);
    for (const [, entry] of this.textureCache) {
      entry.stale = true;
      entry.texture?.dispose();
    }
    this.textureCache.clear();
    this.cubeRT?.dispose();
    this.fallbackTexture.dispose();
    this.material.dispose();
    this.geometry.dispose();
    this.renderer.dispose();
  }

  private bindEvents(): void {
    this.container.addEventListener("pointerdown", (event) => this.onPointerDown(event));
    window.addEventListener("pointermove", (event) => this.onPointerMove(event));
    window.addEventListener("pointerup", (event) => this.onPointerUp(event));
    window.addEventListener("pointercancel", (event) => this.onPointerUp(event));
    this.container.addEventListener("wheel", (event) => this.onWheel(event), { passive: false });
  }

  private onPointerDown(event: PointerEvent): void {
    if (this.activePointerId !== null && this.activePointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    // User drag overrides the cinematic pan — they take control immediately.
    this.cancelIntro();
    this.cancelKenBurns();
    // Re-show the compass any time the user starts interacting.
    this.scheduleCompassFade();
    this.dragging = true;
    this.activePointerId = event.pointerId;
    this.lastMoveTime = performance.now();
    this.velocityLon = 0;
    this.velocityLat = 0;
    this.dragStart = {
      x: event.clientX,
      y: event.clientY,
      lon: this.lon,
      lat: this.lat
    };
    this.container.setPointerCapture?.(event.pointerId);
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.dragging || event.pointerId !== this.activePointerId) {
      return;
    }

    const sensitivity = event.pointerType === "touch" ? 0.19 : 0.13;
    const nextLon = this.dragStart.lon + (this.dragStart.x - event.clientX) * sensitivity;
    const nextLat = this.dragStart.lat + (event.clientY - this.dragStart.y) * sensitivity;
    const now = performance.now();
    const deltaTime = Math.max(1, now - this.lastMoveTime);

    this.velocityLon = (nextLon - this.lon) / deltaTime;
    this.velocityLat = (nextLat - this.lat) / deltaTime;
    this.lon = nextLon;
    this.lat = nextLat;
    this.lastMoveTime = now;
  }

  private onPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.activePointerId) {
      return;
    }

    this.dragging = false;
    this.activePointerId = null;

    try {
      this.container.releasePointerCapture?.(event.pointerId);
    } catch {
      // Pointer capture may already be released by the browser.
    }
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    this.camera.fov = THREE.MathUtils.clamp(this.camera.fov + event.deltaY * 0.035, 42, 92);
    this.camera.updateProjectionMatrix();
  }

  private render(): void {
    // In an immersive XR session the headset owns the camera pose, so the manual
    // drag/momentum + compass logic must stand down (otherwise it fights the rig).
    const presenting = this.renderer.xr.isPresenting;
    if (!presenting) {
      const now = performance.now();
      const deltaTime = Math.max(1, now - this.lastFrameTime);
      this.lastFrameTime = now;

      if (!this.dragging) {
        this.lon += this.velocityLon * deltaTime;
        this.lat += this.velocityLat * deltaTime;
        const damping = 0.78 ** (deltaTime / 16.67);
        this.velocityLon = Math.abs(this.velocityLon) < 0.003 ? 0 : this.velocityLon * damping;
        this.velocityLat = Math.abs(this.velocityLat) < 0.003 ? 0 : this.velocityLat * damping;
      }

      this.updateCamera();
      this.updateCompass();
    }

    this.vrGame?.update();
    this.renderer.render(this.scene, this.camera);

    if (!presenting && this.afterRenderListeners.size > 0) {
      for (const listener of this.afterRenderListeners) {
        listener();
      }
    }
  }

  private updateCamera(): void {
    this.lat = THREE.MathUtils.clamp(this.lat, -84, 84);
    const phi = THREE.MathUtils.degToRad(90 - this.lat);
    const theta = THREE.MathUtils.degToRad(this.lon);
    const target = new THREE.Vector3(
      500 * Math.sin(phi) * Math.cos(theta),
      500 * Math.cos(phi),
      500 * Math.sin(phi) * Math.sin(theta)
    );
    this.camera.lookAt(target);
  }

  private resetView(): void {
    this.cancelIntro();
    this.lon = 180;
    this.lat = 0;
    this.velocityLon = 0;
    this.velocityLat = 0;
    this.camera.fov = 86;
    this.camera.updateProjectionMatrix();
    this.updateCamera();
    // Re-show the compass on every new round so the user knows their bearing.
    this.scheduleCompassFade();
  }

  private applyTexture(texture: THREE.Texture, imageUrl: string | null): void {
    this.configureTexture(texture);
    this.activeTextureUrl = imageUrl;
    this.material.map = texture;
    this.material.needsUpdate = true;
    this.refreshVrBackground();   // keep the in-VR cubemap skybox in sync with the active panorama
  }

  // ---- VR background: cubemap skybox (no equirect pole pinch) -------------
  // VR-only (runs only while presenting). Prefers the 10K /panoramas-10k/ source
  // when present, falling back to the 4K already on the sphere; desktop never touches it.
  private refreshVrBackground(): void {
    if (!this.renderer.xr.isPresenting || !this.material.map) {
      return;
    }
    const url = this.activeTextureUrl;
    const hiUrl = url?.replace("/panoramas/", "/panoramas-10k/");
    if (hiUrl && hiUrl !== url) {
      this.loader.load(
        hiUrl,
        (hi) => {
          if (this.activeTextureUrl !== url || !this.renderer.xr.isPresenting) { hi.dispose(); return; }
          // Missing 10K -> SPA returns index.html (HTTP 200); guard on width so a
          // mis-decoded non-image can't become a black skybox. Real 10K is 10240 wide.
          if (((hi.image as { width?: number })?.width ?? 0) < 8000) { hi.dispose(); this.buildVrCube(this.material.map!, 1024); return; }
          this.configureTexture(hi);
          this.buildVrCube(hi, 2048);   // 10K source -> bigger face (bump to 2560 for full Quest 3 native)
          hi.dispose();                 // equirect not needed after baking to the cube
        },
        undefined,
        () => this.buildVrCube(this.material.map!, 1024)   // load error -> 4K fallback
      );
    } else {
      this.buildVrCube(this.material.map, 1024);
    }
  }

  private buildVrCube(source: THREE.Texture, faceSize: number): void {
    try {
      const previous = this.cubeRT;
      const rt = new THREE.WebGLCubeRenderTarget(faceSize);
      rt.fromEquirectangularTexture(this.renderer, source);
      this.scene.background = rt.texture;
      this.cubeRT = rt;
      this.sphereMesh.visible = false;   // skybox replaces the sphere -> no pole pinch in VR
      previous?.dispose();
    } catch {
      this.scene.background = null;       // conversion failed -> keep the proven inverted-sphere panorama
      this.sphereMesh.visible = true;
    }
  }

  private disableVrBackground(): void {
    this.scene.background = null;
    this.sphereMesh.visible = true;
    this.cubeRT?.dispose();
    this.cubeRT = null;
  }

  private configureTexture(texture: THREE.Texture): void {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();   // full aniso (16 on Quest 2) — sharper at grazing angles, free
  }

  private createFallbackTexture(): THREE.Texture {
    const canvas = document.createElement("canvas");
    canvas.width = 2048;
    canvas.height = 1024;
    const context = canvas.getContext("2d");
    if (!context) {
      return new THREE.CanvasTexture(canvas);
    }

    const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, "#233b55");
    gradient.addColorStop(0.55, "#8299a8");
    gradient.addColorStop(0.56, "#2f3a32");
    gradient.addColorStop(1, "#111814");
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "rgba(255, 255, 255, 0.78)";
    context.font = "700 74px system-ui";
    context.textAlign = "center";
    context.fillText("LatLongAgo", canvas.width / 2, canvas.height / 2);
    return new THREE.CanvasTexture(canvas);
  }

  private getAspectRatio(): number {
    return Math.max(1, this.container.clientWidth) / Math.max(1, this.container.clientHeight);
  }
}
